import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ColumnFiltersState,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetUserSettingsQuery,
  useSetUserSettingMutation,
} from '@bmwe/data/shared';
import { TableView } from '../types/tableView';
import { BmweDataColumn } from '../../../types';

interface TableViewContextValue {
  views: TableView[];
  currentView: TableView;
  setCurrentViewById: (viewId: string) => void;
  updateView: (view: TableView) => void;
  saveView: (view: TableView) => void;
  addView: (view: Omit<TableView, 'id'>) => void;
  deleteView: (viewId: string) => void;
  resetView: (viewId: string) => void;
  /** `onRestore` undoes something outside the view (e.g. the layout) when the snapshot is restored. */
  createSnapshot: (onRestore?: () => void) => void;
  restoreSnapshot: () => void;
  clearSnapshot: () => void;
  markSnapshotPreviewed: () => void;
  hasPreviewedSnapshot: boolean;
  hasSnapshot: boolean;
  sorting: SortingState;
  setSorting: (
    updater: Updater<SortingState> | ((old: SortingState) => SortingState),
  ) => void;
  columnFilters: ColumnFiltersState;
  setFilters: (
    updater:
      | Updater<ColumnFiltersState>
      | ((old: ColumnFiltersState) => ColumnFiltersState),
  ) => void;
  isPending: boolean;
}

interface ActiveViewState {
  viewId: string;
  filters: ColumnFiltersState;
  sorting: SortingState;
}

interface TableViewState {
  views: TableView[];
  currentViewId: string;
  // Ad-hoc filters/sorting of the current view; never persisted.
  active?: ActiveViewState;
}

interface StorageState {
  views: TableView[];
  currentViewId: string;
}

const TableViewContext = createContext<TableViewContextValue | undefined>(
  undefined,
);

interface TableViewProviderProps<TRow extends Record<string, unknown>> {
  tableKey: string;
  defaultViews?: TableView[];
  initialSort?: SortingState;
  initialFilters?: ColumnFiltersState;
  children: ReactNode;
  columns: BmweDataColumn<TRow>[];
}

const DEFAULT_VIEW_ID = 'default';
const EMPTY_FILTERS: ColumnFiltersState = [];
const EMPTY_SORTING: SortingState = [];

// Ad-hoc state applies while it belongs to the current view.
function resolveActive(state: TableViewState): ActiveViewState | undefined {
  const view =
    state.views.find((v) => v.id === state.currentViewId) ?? state.views[0];
  if (!view) {
    return undefined;
  }

  if (state.active?.viewId === view.id) {
    return state.active;
  }

  return {
    viewId: view.id,
    filters: view.filters ?? EMPTY_FILTERS,
    sorting: view.sorting ?? EMPTY_SORTING,
  };
}

function createDefaultView(
  allColumnsVisible: Record<string, boolean>,
  initialSort?: SortingState,
  initialFilters?: ColumnFiltersState,
): TableView {
  return {
    id: DEFAULT_VIEW_ID,
    name: 'Standardansicht',
    visibleColumns: allColumnsVisible,
    isSystem: true,
    sorting: initialSort || [],
    filters: initialFilters || [],
  };
}

function parseStorageState(value: string | null | undefined): StorageState {
  const emptyState: StorageState = {
    views: [],
    currentViewId: '',
  };

  if (!value) {
    return emptyState;
  }

  try {
    const parsed = JSON.parse(value) as StorageState;

    if (!parsed || !Array.isArray(parsed.views)) {
      return emptyState;
    }

    return parsed;
  } catch (error) {
    console.error('Failed to parse storage state:', error);
    return emptyState;
  }
}

function getSystemViews(
  defaultViews: TableView[],
  allColumnsVisible: Record<string, boolean>,
  allColumnsHidden: Record<string, boolean>,
  initialSort?: SortingState,
  initialFilters?: ColumnFiltersState,
): TableView[] {
  if (defaultViews.length > 0) {
    return defaultViews.map((view) => ({
      ...view,
      visibleColumns: { ...allColumnsHidden, ...view.visibleColumns },
      isSystem: true,
    }));
  }

  return [createDefaultView(allColumnsVisible, initialSort, initialFilters)];
}

// Stored views with a system view's id override it; the name stays the code's.
function mergeViews(
  systemViews: TableView[],
  storedViews: TableView[],
  allColumnsHidden: Record<string, boolean>,
): TableView[] {
  const stored = storedViews.map((view) => ({
    ...view,
    visibleColumns: { ...allColumnsHidden, ...view.visibleColumns },
  }));
  const systemIds = new Set(systemViews.map((view) => view.id));

  return [
    ...systemViews.map((view) => {
      const override = stored.find((v) => v.id === view.id);
      return override
        ? { ...override, name: view.name, isSystem: true, hasOverride: true }
        : view;
    }),
    ...stored.filter((view) => !systemIds.has(view.id)),
  ];
}

function toStorageState(
  views: TableView[],
  currentViewId: string,
): StorageState {
  return {
    views: views
      .filter((view) => !view.isSystem || view.hasOverride)
      .map(
        ({ isSystem: _isSystem, hasOverride: _hasOverride, ...view }) => view,
      ),
    currentViewId,
  };
}

function createCompleteMap<TRow extends Record<string, unknown>>(
  columns: BmweDataColumn<TRow>[],
  visible = true,
) {
  return columns.reduce((prev, curr) => {
    const key = curr.id ?? curr.field;
    if (!key) {
      return prev;
    }

    return { ...prev, [key]: visible };
  }, {});
}

export function TableViewProvider<TRow extends Record<string, unknown>>({
  tableKey,
  defaultViews = [],
  initialSort = [],
  initialFilters = [],
  columns,
  children,
}: TableViewProviderProps<TRow>) {
  const storageKey = tableKey;
  const queryClient = useQueryClient();
  const allColumnsVisible = useMemo(
    () => createCompleteMap(columns),
    [columns],
  );
  const allColumnsHidden = useMemo(
    () => createCompleteMap(columns, false),
    [columns],
  );

  // Fetch settings from backend
  const { data: userSettingData, isPending: isLoadingSettings } =
    useGetUserSettingsQuery(
      { key: storageKey },
      {
        retry: 1,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    );

  // Mutation for saving settings to backend
  const { mutate: saveToBackend } = useSetUserSettingMutation({
    onSuccess: async () => {
      // Invalidate and refetch the user settings query
      await queryClient.invalidateQueries({
        queryKey: ['GetUserSettings', { key: storageKey }],
      });
    },
    onError: (error) => {
      console.error('Failed to save table view settings:', error);
    },
  });

  const [snapshot, setSnapshot] = useState<TableViewState | null>(null);
  const snapshotUndoRef = useRef<(() => void)[]>([]);
  const [hasPreviewedSnapshot, setHasPreviewedSnapshot] = useState(false);

  const [state, setState] = useState<TableViewState>(() => {
    try {
      const backendStorageState = parseStorageState(
        userSettingData?.userSetting?.value,
      );
      const systemViews = getSystemViews(
        defaultViews,
        allColumnsVisible,
        allColumnsHidden,
        initialSort,
        initialFilters,
      );
      const mergedViews = mergeViews(
        systemViews,
        backendStorageState.views,
        allColumnsHidden,
      );

      if (mergedViews.length === 0) {
        const initialViews = [
          createDefaultView(allColumnsVisible, initialSort, initialFilters),
        ];
        return {
          views: initialViews,
          currentViewId: initialViews[0].id,
        };
      }

      const storedViewExists = mergedViews.some(
        (view) => view.id === backendStorageState.currentViewId,
      );

      return {
        views: mergedViews,
        currentViewId: storedViewExists
          ? backendStorageState.currentViewId
          : mergedViews[0].id,
      };
    } catch (error) {
      console.error('Failed to initialize view state:', error);
      const systemViews = getSystemViews(
        defaultViews,
        allColumnsVisible,
        allColumnsHidden,
        initialSort,
        initialFilters,
      );
      return {
        views: systemViews,
        currentViewId: systemViews[0].id,
      };
    }
  });

  // Update state when backend data changes
  useEffect(() => {
    if (!isLoadingSettings && userSettingData) {
      const newBackendState = parseStorageState(
        userSettingData.userSetting?.value,
      );

      const systemViews = getSystemViews(
        defaultViews,
        allColumnsVisible,
        allColumnsHidden,
        initialSort,
        initialFilters,
      );
      const mergedViews = mergeViews(
        systemViews,
        newBackendState.views,
        allColumnsHidden,
      );

      if (mergedViews.length === 0) {
        const initialViews = [
          createDefaultView(allColumnsVisible, initialSort, initialFilters),
        ];
        setState({
          views: initialViews,
          currentViewId: initialViews[0].id,
        });
        return;
      }

      setState((prevState) => {
        const storedViewExists = mergedViews.some(
          (view) => view.id === newBackendState.currentViewId,
        );

        return {
          ...prevState,
          views: mergedViews,
          currentViewId: storedViewExists
            ? newBackendState.currentViewId
            : prevState.currentViewId,
        };
      });
    }
    // Only depend on userSettingData?.userSetting?.value to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettingData?.userSetting?.value, isLoadingSettings]);

  const { views, currentViewId } = state;

  const findCurrentView = (): TableView => {
    const foundView = views.find((v) => v.id === currentViewId);

    if (foundView) {
      return foundView;
    }

    if (views.length > 0) {
      return views[0];
    }

    return createDefaultView(allColumnsVisible);
  };

  const currentView = findCurrentView();

  const active = resolveActive(state);
  const sorting = active?.sorting ?? EMPTY_SORTING;
  const columnFilters = active?.filters ?? EMPTY_FILTERS;

  const persist = useCallback(
    (allViews: TableView[], viewId: string) => {
      saveToBackend({
        input: {
          key: storageKey,
          value: JSON.stringify(toStorageState(allViews, viewId)),
        },
      });
    },
    [saveToBackend, storageKey],
  );

  const setFilters = useCallback(
    (
      updater:
        | Updater<ColumnFiltersState>
        | ((old: ColumnFiltersState) => ColumnFiltersState),
    ) => {
      setState((prevState) => {
        const prevActive = resolveActive(prevState);
        if (!prevActive) {
          return prevState;
        }

        const filters =
          typeof updater === 'function' ? updater(prevActive.filters) : updater;

        return { ...prevState, active: { ...prevActive, filters } };
      });
    },
    [],
  );

  const setSorting = useCallback(
    (
      updater: Updater<SortingState> | ((old: SortingState) => SortingState),
    ) => {
      setState((prevState) => {
        const prevActive = resolveActive(prevState);
        if (!prevActive) {
          return prevState;
        }

        const sorting =
          typeof updater === 'function' ? updater(prevActive.sorting) : updater;

        return { ...prevState, active: { ...prevActive, sorting } };
      });
    },
    [],
  );

  const updateView = useCallback((updatedView: TableView) => {
    setState((prevState) => ({
      ...prevState,
      views: prevState.views.map((v) =>
        v.id === updatedView.id ? updatedView : v,
      ),
    }));
  }, []);

  const saveView = useCallback(
    (viewToSave: TableView) => {
      const saved = viewToSave.isSystem
        ? { ...viewToSave, hasOverride: true }
        : viewToSave;

      setState((prevState) => {
        const updatedViews = prevState.views.map((v) =>
          v.id === saved.id ? saved : v,
        );
        persist(updatedViews, prevState.currentViewId);

        return {
          ...prevState,
          views: updatedViews,
          active:
            prevState.active?.viewId === saved.id
              ? undefined
              : prevState.active,
        };
      });
      setSnapshot(null);
      setHasPreviewedSnapshot(false);
      snapshotUndoRef.current = [];
    },
    [persist],
  );

  const addView = useCallback(
    (viewData: Omit<TableView, 'id'>) => {
      const newView: TableView = {
        ...viewData,
        id: `view-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        canEdit: true,
      };

      setState((prevState) => {
        const updatedViews = [...prevState.views, newView];
        const newState = {
          views: updatedViews,
          currentViewId: newView.id,
        };

        persist(updatedViews, newView.id);

        return newState;
      });
      setSnapshot(null);
      setHasPreviewedSnapshot(false);
      snapshotUndoRef.current = [];
    },
    [persist],
  );

  const deleteView = useCallback(
    (viewId: string) => {
      const viewToDelete = views.find((v) => v.id === viewId);

      if (!viewToDelete || viewToDelete.isSystem) {
        return;
      }

      setState((prevState) => {
        const filteredViews = prevState.views.filter((v) => v.id !== viewId);
        const newViews =
          filteredViews.length === 0
            ? [createDefaultView(allColumnsVisible)]
            : filteredViews;

        const newCurrentViewId =
          prevState.currentViewId === viewId
            ? newViews[0].id
            : prevState.currentViewId;

        persist(newViews, newCurrentViewId);

        return {
          views: newViews,
          currentViewId: newCurrentViewId,
        };
      });
    },
    [allColumnsVisible, views, persist],
  );

  const resetView = useCallback(
    (viewId: string) => {
      const codeView = getSystemViews(
        defaultViews,
        allColumnsVisible,
        allColumnsHidden,
        initialSort,
        initialFilters,
      ).find((v) => v.id === viewId);

      if (!codeView) {
        return;
      }

      setState((prevState) => {
        const updatedViews = prevState.views.map((v) =>
          v.id === viewId ? codeView : v,
        );
        persist(updatedViews, prevState.currentViewId);

        return {
          ...prevState,
          views: updatedViews,
          active:
            prevState.active?.viewId === viewId ? undefined : prevState.active,
        };
      });
      setSnapshot(null);
      setHasPreviewedSnapshot(false);
      snapshotUndoRef.current = [];
    },
    [
      defaultViews,
      allColumnsVisible,
      allColumnsHidden,
      initialSort,
      initialFilters,
      persist,
    ],
  );

  // Snapshot management methods
  const createSnapshot = useCallback(
    (onRestore?: () => void) => {
      if (onRestore) snapshotUndoRef.current.push(onRestore);
      setSnapshot((current) => {
        if (current !== null) {
          return current;
        }

        setHasPreviewedSnapshot(false);
        return structuredClone(state);
      });
    },
    [state],
  );

  const restoreSnapshot = useCallback(() => {
    if (!snapshot) {
      return;
    }

    setState(snapshot);
  }, [snapshot]);

  const clearSnapshot = useCallback(() => {
    if (snapshot) {
      setState(snapshot);
      snapshotUndoRef.current.forEach((undo) => undo());
    }
    snapshotUndoRef.current = [];
    setSnapshot(null);
    setHasPreviewedSnapshot(false);
  }, [snapshot]);

  const markSnapshotPreviewed = useCallback(() => {
    setHasPreviewedSnapshot(true);
  }, []);

  const hasSnapshot = snapshot !== null;

  const setCurrentViewById = useCallback(
    (viewId: string) => {
      clearSnapshot();
      setState((prevState) => {
        // (Re)opening a view drops ad-hoc changes so its saved state applies.
        const newState = {
          ...prevState,
          currentViewId: viewId,
          active: undefined,
        };

        persist(prevState.views, viewId);

        return newState;
      });
    },
    [clearSnapshot, persist],
  );

  const contextValue = useMemo<TableViewContextValue>(
    () => ({
      views,
      currentView,
      setCurrentViewById,
      updateView,
      saveView,
      addView,
      deleteView,
      resetView,
      createSnapshot,
      restoreSnapshot,
      clearSnapshot,
      markSnapshotPreviewed,
      hasPreviewedSnapshot,
      hasSnapshot,
      sorting,
      setSorting,
      columnFilters,
      setFilters,
      isPending: isLoadingSettings,
    }),
    [
      views,
      currentView,
      setCurrentViewById,
      updateView,
      saveView,
      addView,
      deleteView,
      resetView,
      createSnapshot,
      restoreSnapshot,
      clearSnapshot,
      markSnapshotPreviewed,
      hasPreviewedSnapshot,
      hasSnapshot,
      sorting,
      setSorting,
      columnFilters,
      setFilters,
      isLoadingSettings,
    ],
  );

  return (
    <TableViewContext.Provider value={contextValue}>
      {children}
    </TableViewContext.Provider>
  );
}

export function useTableView(): TableViewContextValue {
  const context = useContext(TableViewContext);

  if (!context) {
    throw new Error('useTableView must be used within a TableViewProvider');
  }

  return context;
}
